import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Zap, Save, Loader2, BookmarkCheck } from 'lucide-react';
import { SiteSelector } from './SiteSelector';
import { MonthlyDataTable } from './MonthlyDataTable';
import { ScenarioList } from './ScenarioList';
import { BankCostPanel } from './BankCostPanel';
import { ComparisonTable } from './ComparisonTable';
import { supabase } from '../../lib/supabase';
import type { MonthlyBucket, RateScenario, BankCostConfig } from '../../types';

const defaultBankCost: BankCostConfig = {
  enabled: false,
  paymentFixedFee: 0,
  paymentRatePercent: 0,
  payoutFixedFee: 0,
  payoutRatePercent: 0,
};

function makeScenario(index: number): RateScenario {
  return {
    id: crypto.randomUUID(),
    name: `Scenario ${index}`,
    paymentFixedFee: 0,
    paymentRatePercent: 0,
    payoutFixedFee: 0,
    payoutRatePercent: 0,
  };
}

export function QuickQuote() {
  const [selectedSid, setSelectedSid] = useState<number | null>(null);
  const [siteName, setSiteName] = useState('');
  const [months, setMonths] = useState<MonthlyBucket[]>([]);
  const [loading, setLoading] = useState(false);
  const [scenarios, setScenarios] = useState<RateScenario[]>([makeScenario(1)]);
  const [bankCost, setBankCost] = useState<BankCostConfig>(defaultBankCost);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [quoteName, setQuoteName] = useState('');
  const [monthCount, setMonthCount] = useState(12);

  const loadMonthlyData = useCallback(async (sid: number, numMonths: number) => {
    setLoading(true);
    try {
      const res = await fetch(
        `/api/v1/transactions/monthly-summary?sid=${sid}&months=${numMonths}`
      );
      if (!res.ok) throw new Error('Failed to fetch monthly data');
      const data = await res.json();
      setMonths(data.months || []);
    } catch (err) {
      console.error(err);
      setMonths([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSiteSelect = useCallback(
    (sid: number, name: string) => {
      setSelectedSid(sid);
      setSiteName(name);
      setSaved(false);
      loadMonthlyData(sid, monthCount);
    },
    [loadMonthlyData, monthCount]
  );

  const handleMonthCountChange = useCallback(
    (newCount: number) => {
      setMonthCount(newCount);
      if (selectedSid) {
        loadMonthlyData(selectedSid, newCount);
      }
    },
    [selectedSid, loadMonthlyData]
  );

  const handleSave = async () => {
    if (!selectedSid || !quoteName.trim()) return;
    setSaving(true);
    try {
      const user = (await supabase.auth.getUser()).data.user;
      const results = computeAllResults();
      const { error } = await supabase.from('quick_quotes').insert({
        name: quoteName.trim(),
        sid: selectedSid,
        site_name: siteName,
        months,
        scenarios,
        bank_cost: bankCost,
        results,
        created_by: user?.id,
      });
      if (error) throw error;
      setSaved(true);
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  function computeAllResults() {
    return scenarios.map((sc) => ({
      scenarioId: sc.id,
      scenarioName: sc.name,
      months: months.map((m) => {
        const paymentRev =
          m.payment.count * sc.paymentFixedFee +
          m.payment.totalAmount * (sc.paymentRatePercent / 100);
        const payoutRev =
          m.payout.count * sc.payoutFixedFee +
          m.payout.totalAmount * (sc.payoutRatePercent / 100);
        const totalRev = paymentRev + payoutRev;

        let bankPayment = 0;
        let bankPayout = 0;
        if (bankCost.enabled) {
          bankPayment =
            m.payment.count * bankCost.paymentFixedFee +
            m.payment.totalAmount * (bankCost.paymentRatePercent / 100);
          bankPayout =
            m.payout.count * bankCost.payoutFixedFee +
            m.payout.totalAmount * (bankCost.payoutRatePercent / 100);
        }
        const totalBankCost = bankPayment + bankPayout;
        const profit = totalRev - totalBankCost;

        return {
          month: m.month,
          paymentRev,
          payoutRev,
          totalRev,
          bankPayment,
          bankPayout,
          totalBankCost,
          profit,
        };
      }),
    }));
  }

  const hasData = months.length > 0 && scenarios.length > 0;

  return (
    <div className="space-y-6">
      <div className="page-header">
        <div>
          <div className="flex items-center gap-3">
            <Zap className="w-6 h-6 text-amber-500" />
            <h1 className="page-title">Quick Quote</h1>
          </div>
          <p className="page-subtitle">
            Compare rate scenarios month-by-month using aggregate transaction data.
          </p>
        </div>
        <Link to="/quick-quote/saved" className="btn-secondary">
          <BookmarkCheck className="w-4 h-4" />
          Saved Quotes
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end gap-3 sm:gap-4">
          <div className="flex-1">
            <SiteSelector onSelect={handleSiteSelect} selectedSid={selectedSid} />
          </div>
          <div className="w-full sm:w-40">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Months of Data
            </label>
            <select
              value={monthCount}
              onChange={(e) => handleMonthCountChange(Number(e.target.value))}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
            >
              {[3, 6, 9, 12, 18, 24].map((n) => (
                <option key={n} value={n}>
                  {n} months
                </option>
              ))}
            </select>
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            <span className="ml-3 text-gray-600">Loading monthly data...</span>
          </div>
        )}

        {!loading && months.length > 0 && (
          <>
            <MonthlyDataTable months={months} />
            <ScenarioList scenarios={scenarios} onChange={setScenarios} />
            <BankCostPanel config={bankCost} onChange={setBankCost} />
          </>
        )}
      </div>

      {hasData && !loading && (
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
          <ComparisonTable
            months={months}
            scenarios={scenarios}
            bankCost={bankCost}
          />
        </div>
      )}

      {hasData && !loading && (
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Save Quick Quote</h3>
          <div className="flex flex-col sm:flex-row sm:items-end gap-3 sm:gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quote Name
              </label>
              <input
                type="text"
                value={quoteName}
                onChange={(e) => setQuoteName(e.target.value)}
                placeholder="e.g. Merchant X - Rate Comparison"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <button
                onClick={handleSave}
                disabled={saving || !quoteName.trim() || saved}
                className="btn-primary flex-1 sm:flex-initial"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {saved ? 'Saved!' : 'Save'}
              </button>
              {saved && (
                <Link to="/quick-quote/saved" className="btn-secondary flex-1 sm:flex-initial">
                  <BookmarkCheck className="w-4 h-4" />
                  View Saved
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
